import React, { createElement, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Form } from "antd";
import { DynamicFormProps } from "./dynamicForm.model";
import { DynamicStep } from "./DynamicStep";
import DynamicFormContextProvider from "./DynamicFormContext";
import { Button } from "@companion-core/shared/app/Components/Button";
import { Summary } from "@companion-core/shared/app/Components/DynamicForm/Summary";
import { useDynamicForm } from "@companion-core/shared/app/Components/DynamicForm/hooks/dynamicForm";
import { hasOnlyOneRadioShape } from "@companion-core/shared/app/Utils/dynamicForm";
import "@companion-core/web/src/Assets/Styles/scss/dynamicForm.scss";
import "@companion-core/web/src/Assets/Styles/scss/global.scss";

const STEP_HEADING_ID = "dynamic-form__step-heading";

/**
 * DynamicForm is a React component that renders a dynamic form based on the provided JSON configuration.
 * It supports multiple steps, validation, and summary.
 *
 * **You should not give different initialFormJSON to the same dynamic form.**
 * *Eventually, you should be able to use the same dynamic form with different initialFormJSON
 * if you reset it or if you have just one step.*
 *
 * @component
 * @param {Object} props - The properties that define the DynamicForm component.
 * @param {string} props.name - The name of the form.
 * @param {Object} props.initialFormJSON - The initial JSON configuration of the form.
 * @param {boolean} [props.useProvider=false] - Determines whether to use the wizard store with provider.
 * @param {Function} props.onSubmitForm - The function to be called when the form is submitted.
 * @param {Function} [props.onSubmitConclude] - The function to be called after the form submission is concluded.
 *
 * @returns {React.ReactElement} The DynamicForm component.
 */
export const DynamicForm = ({
  name,
  initialFormJSON,
  useProvider = false,
  onSubmitForm,
  onSubmitConclude,
  titleTag = "h2",
  submitButtonAccessibilityLabel,
}: DynamicFormProps): React.ReactElement => {
  const {
    t,
    submitButtonLoading,
    currentForm,
    currentStep,
    isFinalStep,
    dynamicStepRef,
    incrementStep,
    submitForm,
  } = useDynamicForm({ initialFormJSON, useProvider, onSubmitForm, onSubmitConclude });

  const stepHeadingRef = useRef<HTMLHeadingElement>(null);
  const isFirstValidationAttemptOnCurrentStepRef = useRef(true);
  const [fieldValidationVersion, setFieldValidationVersion] = useState(0);
  const [describedErrorIds, setDescribedErrorIds] = useState<string[]>([]);
  const [suppressNewErrorsInDescription, setSuppressNewErrorsInDescription] = useState(false);
  const handleFieldValidationChange = useCallback(() => {
    // This is used to detect field errors changes and force the re-rendering of the accessibility labels
    setFieldValidationVersion((version) => version + 1);
  }, []);
  const getCurrentStepErrorIds = useCallback(() => {
    if (!currentStep) return [];
    return currentStep.fields
      .filter((field) => field.name && field.fieldError)
      .map((field) => `${field.name}-error`);
  }, [currentStep]);

  useEffect(() => {
    // clear the errors from the current step when navigating between steps
    currentStep?.fields.forEach((field) => {
      delete field.fieldError;
      delete field.errors;
    });
    isFirstValidationAttemptOnCurrentStepRef.current = true;
    setSuppressNewErrorsInDescription(false);
    setDescribedErrorIds([]);
    if (currentStep?.title) {
      stepHeadingRef.current?.focus({ preventScroll: true });
    }
  }, [currentForm?.jsonPath, currentStep?.order, currentStep?.title]);

  useEffect(() => {
    // we need to update the first validation attempt depending on the presence of errors
    const currentErrorIds = getCurrentStepErrorIds();
    const hasErrors = currentErrorIds.length > 0;
    isFirstValidationAttemptOnCurrentStepRef.current = !hasErrors;
    if (!isFirstValidationAttemptOnCurrentStepRef.current && !suppressNewErrorsInDescription) {
      setDescribedErrorIds(getCurrentStepErrorIds());
    }
  }, [fieldValidationVersion, getCurrentStepErrorIds, suppressNewErrorsInDescription]);

  const incrementStepWithErrorHandling = useCallback(() => {
    const alreadyAnnouncedErrorIds = isFirstValidationAttemptOnCurrentStepRef.current
      ? []
      : getCurrentStepErrorIds();
    isFirstValidationAttemptOnCurrentStepRef.current = false;
    incrementStep({
      onInvalidStep: () => {
        const currentErrorIds = getCurrentStepErrorIds();
        const oldErrorIds = currentErrorIds.filter((errorId) =>
          alreadyAnnouncedErrorIds.includes(errorId)
        );
        if (oldErrorIds.length > 0) {
          setSuppressNewErrorsInDescription(true);
          setDescribedErrorIds(oldErrorIds);
        } else {
          setDescribedErrorIds([]);
        }

        // redirect to the first input in the field with an error
        const firstFieldWithError = currentStep?.fields.find(
          (field) => field?.errors?.length && field?.errors?.length > 0
        );
        if (!firstFieldWithError?.testID) return;

        // find the element with the corresponding testID
        const root = document.querySelector(
          `[data-testid*="${firstFieldWithError.testID}"]`
        ) as HTMLElement | null;
        let el: HTMLElement | null =
          (root?.querySelector("input, textarea, select") as HTMLElement | null) || root;
        if (firstFieldWithError.type === "counters") {
          // find the first counter button that is not disabled
          el = el?.querySelector("button:not(:disabled)") as HTMLElement | null;
        }

        el?.focus();
        if (el?.tagName === "BUTTON") {
          // add focus-visible class to the button (custom focus visible class for accessibility)
          (el as HTMLElement).classList.add("focus-visible");
        }
        if (el) {
          const focusedElement = el;
          const handleFocusOut = () => {
            setSuppressNewErrorsInDescription(false);
            setDescribedErrorIds(getCurrentStepErrorIds());
            focusedElement.removeEventListener("focusout", handleFocusOut);
          };
          focusedElement.addEventListener("focusout", handleFocusOut);
        }
      },
      onValidStep: () => {
        stepHeadingRef.current?.focus({ preventScroll: true });
      },
    });
  }, [incrementStep, currentStep, getCurrentStepErrorIds]);

  const titleElement = createElement(
    titleTag,
    {
      ref: stepHeadingRef,
      id: STEP_HEADING_ID,
      tabIndex: -1,
      className: "dynamic-form__title title-6",
    },
    t(`${currentStep?.title}`)
  );

  const stepErrorIds = useMemo(() => {
    if (!suppressNewErrorsInDescription || !describedErrorIds.length) {
      return undefined;
    }
    return describedErrorIds.join(" ");
  }, [describedErrorIds, suppressNewErrorsInDescription]);

  return (
    <Form
      data-testid={`dynamic-form--${name}`}
      name={name}
      layout="vertical"
      autoComplete="off"
      requiredMark="optional"
      className={`dynamic-form ${currentStep && hasOnlyOneRadioShape(currentStep) ? "dynamic-form--full-height" : ""}`}>
      {currentForm && currentStep?.title && titleElement}
      {currentForm &&
        currentStep &&
        (currentStep.type !== "summary" ? (
          <section
            aria-labelledby={STEP_HEADING_ID}
            {...(stepErrorIds ? { "aria-describedby": stepErrorIds } : {})}>
            <DynamicFormContextProvider
              toNextStep={
                currentStep && !isFinalStep ? incrementStepWithErrorHandling : submitForm
              }>
              <DynamicStep
                key={`step--${currentStep.order}`}
                step={currentStep}
                ref={dynamicStepRef}
                onFieldValidationChange={handleFieldValidationChange}
              />
            </DynamicFormContextProvider>
          </section>
        ) : (
          <Summary aria-labelledby={STEP_HEADING_ID} />
        ))}
      {currentStep && !hasOnlyOneRadioShape(currentStep) && (
        <div className="dynamic-form__button-container">
          {!isFinalStep && (
            <Form.Item className="margin-left-auto margin-top-auto">
              <Button
                testID={`dynamic-form__button-next--${name}`}
                htmlType="submit"
                type="primary"
                label={t("dynamic-form.button.next")}
                accessibilityLabel={
                  currentStep?.nextButtonAccessibilityLabel
                    ? t(currentStep.nextButtonAccessibilityLabel)
                    : undefined
                }
                onClick={incrementStepWithErrorHandling}
              />
            </Form.Item>
          )}
          {isFinalStep && (
            <Form.Item className="margin-top-auto">
              <Button
                testID={`dynamic-form__button-submit--${name}`}
                htmlType="submit"
                type="primary"
                icon={currentForm?.submitIcon}
                label={
                  currentForm?.submitLabel
                    ? t(currentForm?.submitLabel)
                    : t("dynamic-form.button.submit")
                }
                loading={submitButtonLoading}
                accessibilityLabel={submitButtonAccessibilityLabel}
                onClick={submitForm}
              />
            </Form.Item>
          )}
        </div>
      )}
    </Form>
  );
};
